/**
 * NotificationsForm — Settings → Notifications.
 *
 * @see 08-system-decisions.md §4 — WhatsApp + email default-on, in-app log always-on,
 * SMS reserved for OTP/security/critical notices.
 */
import { Bell, BellOff, Mail, MessageCircle, ShieldAlert } from "lucide-react";
import type * as React from "react";
import {
  Alert,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Switch,
  cn,
} from "@flint/ui";
import { FormCard } from "../form-card.tsx";
import {
  type NotificationEventValues,
  type NotificationsFormValues,
} from "./lib/form-defaults.ts";

export interface NotificationsFormProps {
  values: NotificationsFormValues;
  serverError?: string | null;
  onChange: (next: NotificationsFormValues) => void;
}

type ExternalChannel = "whatsapp" | "email" | "sms";
type FallbackChannel = ExternalChannel | "none";

export function NotificationsForm({ values, serverError, onChange }: NotificationsFormProps) {
  const enabled = values.notificationsEnabled;
  const updateEvent = (eventKey: string, patch: Partial<NotificationEventValues>) => {
    onChange({
      ...values,
      events: values.events.map((event) =>
        event.eventKey === eventKey ? normaliseEvent({ ...event, ...patch }) : event,
      ),
    });
  };

  return (
    <>
      {serverError ? (
        <Alert intent="bad" title="Couldn't save notification settings" description={serverError} />
      ) : null}

      <FormCard
        id="master-switch"
        icon={enabled ? <Bell size={14} aria-hidden /> : <BellOff size={14} aria-hidden />}
        title="Outbound notifications"
        description="Master control for automated customer, vendor, approver, compliance, and system messages."
        badge={
          enabled ? (
            <StatusPill tone="ok" size="xs">On</StatusPill>
          ) : (
            <StatusPill tone="warn" size="xs">Off</StatusPill>
          )
        }
      >
        <ToggleRow
          title={enabled ? "Notifications are on" : "Notifications are off"}
          description={
            enabled
              ? "Flint sends enabled events through their configured channels."
              : "External sends are suppressed. OTP and in-app logs remain available."
          }
          checked={enabled}
          onCheckedChange={(next) => onChange({ ...values, notificationsEnabled: next })}
        />
        <p className="mt-3 text-xs leading-relaxed text-muted">
          The in-app log is always kept for audit and recovery. SMS is intentionally limited to
          critical or security events so routine reminders do not burn expensive credits.
        </p>
      </FormCard>

      <FormCard
        id="event-routing"
        icon={<MessageCircle size={14} aria-hidden />}
        title="Event routing"
        description="Choose which business events can send externally and which channel they try first."
      >
        <div className="grid gap-3">
          {values.events.map((event) => (
            <EventRoutingRow
              key={event.eventKey}
              event={event}
              onChange={(patch) => updateEvent(event.eventKey, patch)}
            />
          ))}
        </div>
      </FormCard>

      <FormCard
        id="reminder-cadences"
        icon={<Mail size={14} aria-hidden />}
        title="Reminder cadences"
        description="Set how overdue and compliance reminder nudges repeat. Events without reminders stay fixed."
      >
        <div className="grid gap-3 md:grid-cols-2">
          {values.events
            .filter((event) => event.supportsReminders)
            .map((event) => (
              <ReminderCard
                key={event.eventKey}
                event={event}
                onChange={(patch) => updateEvent(event.eventKey, patch)}
              />
            ))}
        </div>
      </FormCard>
    </>
  );
}

function EventRoutingRow(props: {
  event: NotificationEventValues;
  onChange: (patch: Partial<NotificationEventValues>) => void;
}) {
  const critical = props.event.severity === "critical";
  return (
    <div className={cn("rounded-md border border-line bg-paper p-3", !props.event.enabled && "opacity-70")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-ink">{props.event.label}</p>
            <StatusPill tone={severityTone(props.event.severity)} size="xs" dot>
              {props.event.severity}
            </StatusPill>
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">{props.event.description}</p>
        </div>
        <ToggleMini
          checked={props.event.enabled}
          label={props.event.enabled ? "On" : "Off"}
          onChange={(enabled) => props.onChange({ enabled })}
        />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_1fr_1.2fr]">
        <ChannelChecks event={props.event} onChange={props.onChange} />
        <Field helper="First external channel Flint tries when this event sends.">
          <Label>Primary</Label>
          <Select
            value={props.event.primaryChannel}
            onValueChange={(value) => props.onChange({ primaryChannel: value as ExternalChannel })}
          >
            <SelectTrigger className="h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              {critical ? <SelectItem value="sms">SMS</SelectItem> : null}
            </SelectContent>
          </Select>
        </Field>
        <Field helper="Used if the primary channel is disabled or unavailable.">
          <Label>Fallback</Label>
          <Select
            value={props.event.fallbackChannel}
            onValueChange={(value) => props.onChange({ fallbackChannel: value as FallbackChannel })}
          >
            <SelectTrigger className="h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              {critical ? <SelectItem value="sms">SMS</SelectItem> : null}
              <SelectItem value="none">No fallback</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function ChannelChecks(props: {
  event: NotificationEventValues;
  onChange: (patch: Partial<NotificationEventValues>) => void;
}) {
  const critical = props.event.severity === "critical";
  return (
    <Field helper="In-app log is always on and cannot be disabled.">
      <Label>Allowed channels</Label>
      <div className="flex flex-wrap gap-2">
        <ChannelButton
          icon={<MessageCircle size={13} aria-hidden />}
          label="WhatsApp"
          checked={props.event.whatsappEnabled}
          onClick={() => props.onChange({ whatsappEnabled: !props.event.whatsappEnabled })}
        />
        <ChannelButton
          icon={<Mail size={13} aria-hidden />}
          label="Email"
          checked={props.event.emailEnabled}
          onClick={() => props.onChange({ emailEnabled: !props.event.emailEnabled })}
        />
        <ChannelButton
          icon={<ShieldAlert size={13} aria-hidden />}
          label="SMS"
          checked={props.event.smsEnabled}
          disabled={!critical}
          {...(!critical ? { title: "SMS is reserved for critical and security events" } : {})}
          onClick={() => props.onChange({ smsEnabled: !props.event.smsEnabled })}
        />
        <span className="inline-flex min-h-10 items-center rounded-md border border-line bg-bg px-3 text-xs font-semibold text-muted">
          In-app
        </span>
      </div>
    </Field>
  );
}

function ReminderCard(props: {
  event: NotificationEventValues;
  onChange: (patch: Partial<NotificationEventValues>) => void;
}) {
  return (
    <div className="rounded-md border border-line bg-paper p-3">
      <p className="text-sm font-semibold text-ink">{props.event.label}</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <NumberField
          label="First after"
          value={props.event.firstReminderAfterDays}
          onChange={(firstReminderAfterDays) => props.onChange({ firstReminderAfterDays })}
        />
        <NumberField
          label="Repeat"
          value={props.event.repeatEveryDays}
          onChange={(repeatEveryDays) => props.onChange({ repeatEveryDays })}
        />
        <NumberField
          label="Max"
          value={props.event.maxReminderCount}
          onChange={(maxReminderCount) => props.onChange({ maxReminderCount })}
        />
      </div>
      <p className="mt-2 text-xs text-muted">Days after due date · days between nudges · total nudges.</p>
    </div>
  );
}

function NumberField(props: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <Field>
      <Label>{props.label}</Label>
      <Input
        className="h-10"
        inputMode="numeric"
        value={props.value}
        onChange={(event) => props.onChange(Math.max(0, parseInt(event.currentTarget.value, 10) || 0))}
      />
    </Field>
  );
}

function ChannelButton(props: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        "inline-flex min-h-10 items-center gap-1.5 rounded-md border px-3 text-xs font-semibold transition",
        props.checked ? "border-sky-200 bg-sky-50 text-sky-700" : "border-line bg-bg text-muted",
        props.disabled && "cursor-not-allowed opacity-50",
      )}
    >
      {props.icon}
      {props.label}
    </button>
  );
}

function ToggleRow(props: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-line bg-raised p-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{props.title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted">{props.description}</p>
      </div>
      <ToggleSwitch checked={props.checked} ariaLabel="Toggle outbound notifications" onChange={props.onCheckedChange} />
    </div>
  );
}

function ToggleMini(props: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-muted">{props.label}</span>
      <ToggleSwitch checked={props.checked} ariaLabel={props.label} onChange={props.onChange} />
    </div>
  );
}

function ToggleSwitch(props: { checked: boolean; ariaLabel: string; onChange: (checked: boolean) => void }) {
  return <Switch checked={props.checked} onCheckedChange={props.onChange} aria-label={props.ariaLabel} />;
}

function normaliseEvent(event: NotificationEventValues): NotificationEventValues {
  if (event.severity === "critical") return event;
  return {
    ...event,
    smsEnabled: false,
    primaryChannel: event.primaryChannel === "sms" ? "whatsapp" : event.primaryChannel,
    fallbackChannel: event.fallbackChannel === "sms" ? "email" : event.fallbackChannel,
  };
}

function severityTone(severity: NotificationEventValues["severity"]) {
  if (severity === "critical") return "bad";
  if (severity === "important") return "warn";
  return "muted";
}
